import { useState } from "react";
import { Plus } from "lucide-react";

import { EliminarPrecioDeIaDialog } from "@/components/ia/EliminarPrecioDeIaDialog";
import { PrecioDeIaDialog } from "@/components/ia/PrecioDeIaDialog";
import { EstadoDeError } from "@/components/estados/EstadoDeError";
import { EstadoSinConexion } from "@/components/estados/EstadoSinConexion";
import { Button } from "@/components/ui/button";
import { Empty, EmptyDescription, EmptyHeader, EmptyTitle } from "@/components/ui/empty";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { usePreciosDeIa } from "@/hooks/useConsumoDeIa";
import { estadoDeConsulta } from "@/lib/estadoDeConsulta";
import { formatearFecha } from "@/lib/fechas";
import { formatearCostoUsd } from "@/lib/formatoDeConsumo";
import type { IaPrecio } from "@/lib/consumoDeIa";

const FILAS_SKELETON = 4;

/** `"0.7500"` → `"US$ 0,75"` — cada columna de precio ya es "por millón". */
function porMillon(valor: string | null): string {
  return formatearCostoUsd(valor);
}

/** `null` = todo cerrado; `"nuevo"` = alta; un `IaPrecio` = edición de esa fila. */
type EstadoDelDialogo = "cerrado" | "nuevo" | IaPrecio;

/**
 * El catálogo de precios por proveedor, modelo y período de vigencia.
 *
 * EDITAR O BORRAR UN PRECIO NUNCA TOCA UN `IaConsumo` YA REGISTRADO: el costo
 * de cada petición pasada queda FROZEN al momento de la llamada — es la
 * advertencia que llevan los dos diálogos, y por eso se repite acá arriba:
 * quien solo mira la tabla, sin abrir ningún diálogo, tiene que poder leerlo
 * igual antes de tocar nada.
 */
export function CatalogoDePreciosDeIa() {
  const consulta = usePreciosDeIa();
  const estado = estadoDeConsulta(consulta);
  const [dialogo, setDialogo] = useState<EstadoDelDialogo>("cerrado");
  const [aEliminar, setAEliminar] = useState<IaPrecio | null>(null);

  const precios = consulta.data ?? [];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <p className="text-ink-muted max-w-[60ch] text-sm">
          Los precios son en dólares por cada 1.000.000 de tokens. Guardar un cambio acá NO
          recalcula ningún costo ya registrado: cada petición pasada conserva el suyo, congelado
          al momento en que ocurrió.
        </p>
        <Button type="button" onClick={() => setDialogo("nuevo")} className="gap-2">
          <Plus className="size-4" />
          Agregar precio
        </Button>
      </div>

      {estado.sinConexion ? (
        <EstadoSinConexion que="el catálogo de precios de IA" />
      ) : estado.hayError ? (
        <EstadoDeError
          titulo="No pudimos cargar el catálogo de precios"
          error={consulta.error}
          onReintentar={() => consulta.refetch()}
          reintentando={estado.recargando}
        />
      ) : estado.cargando ? (
        <div className="space-y-2">
          {Array.from({ length: FILAS_SKELETON }).map((_, i) => (
            <Skeleton
              key={i}
              role="status"
              aria-label="Cargando el catálogo de precios"
              className="h-11 w-full rounded-md"
            />
          ))}
        </div>
      ) : precios.length === 0 ? (
        <Empty className="rounded-xl border">
          <EmptyHeader>
            <EmptyTitle>Todavía no hay ningún precio cargado</EmptyTitle>
            <EmptyDescription>
              Sin un precio para un modelo, sus peticiones se registran igual pero su costo
              queda en "Sin precio" — nunca en cero.
            </EmptyDescription>
          </EmptyHeader>
        </Empty>
      ) : (
        <div className="bg-card overflow-hidden rounded-xl border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Proveedor</TableHead>
                <TableHead>Modelo</TableHead>
                <TableHead>Vigente desde</TableHead>
                <TableHead className="text-right">USD / 1M entrada</TableHead>
                <TableHead className="text-right">USD / 1M salida</TableHead>
                <TableHead className="text-right">USD / 1M caché</TableHead>
                <TableHead className="w-32 text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {precios.map((p) => (
                <TableRow key={p.id}>
                  <TableCell>{p.proveedor}</TableCell>
                  <TableCell className="font-mono text-sm font-bold">{p.modelo}</TableCell>
                  <TableCell>{formatearFecha(p.vigente_desde)}</TableCell>
                  <TableCell className="text-right font-mono text-xs">
                    {porMillon(p.usd_entrada_millon)}
                  </TableCell>
                  <TableCell className="text-right font-mono text-xs">
                    {porMillon(p.usd_salida_millon)}
                  </TableCell>
                  <TableCell className="text-right font-mono text-xs">
                    {p.usd_cache_millon === null ? "—" : porMillon(p.usd_cache_millon)}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setDialogo(p)}
                      >
                        Editar
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-destructive hover:text-destructive"
                        onClick={() => setAEliminar(p)}
                      >
                        Eliminar
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {dialogo !== "cerrado" ? (
        <PrecioDeIaDialog
          key={dialogo === "nuevo" ? "nuevo" : dialogo.id}
          precio={dialogo === "nuevo" ? null : dialogo}
          onCerrar={() => setDialogo("cerrado")}
        />
      ) : null}

      <EliminarPrecioDeIaDialog
        key={`baja-${aEliminar?.id}`}
        precio={aEliminar}
        onCerrar={() => setAEliminar(null)}
      />
    </div>
  );
}
